import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Typography,
  Box,
  Button,
  Paper,
  CircularProgress,
  Container,
  Divider,
  Grid,
  TextField,
  Alert,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  CardHeader,
  ButtonGroup,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import AssignmentService from "../services/AssignmentService";
import type { Assignment } from "../services/AssignmentService";
import { useSnackbar } from "notistack";

interface CleanlinessItem {
  label: string;
  key: "interior" | "exterior" | "cabine";
  isConform: boolean | null;
}

interface CleanlinessState {
  interior: boolean;
  exterior: boolean;
  cabine?: boolean;
  comments?: string;
  photos: string[];
}

const CleanlinessInspection = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isReturn = searchParams.get("return") === "true";
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [assignment, setAssignment] = useState<Assignment | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // État pour la propreté
  const [cleanliness, setCleanliness] = useState<CleanlinessState>({
    interior: false,
    exterior: false,
    cabine: false,
    comments: "",
    photos: [],
  });

  // Liste des éléments à vérifier
  const cleanlinessItems: CleanlinessItem[] = [
    {
      label: "Intérieur du véhicule",
      key: "interior",
      isConform: cleanliness.interior,
    },
    {
      label: "Extérieur du véhicule",
      key: "exterior",
      isConform: cleanliness.exterior,
    },
    {
      label: "Cabine du conducteur",
      key: "cabine",
      isConform: cleanliness.cabine !== undefined ? cleanliness.cabine : null,
    },
  ];

  // État pour l'ajout d'une nouvelle photo
  const [newPhoto, setNewPhoto] = useState("");
  const [isCameraActive, setIsCameraActive] = useState(false);

  // Charger les détails de l'affectation
  useEffect(() => {
    const fetchAssignment = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const data = await AssignmentService.getById(id);
        console.log("[CleanlinessInspection] Affectation récupérée:", data);

        // Vérifier si l'affectation est dans la bonne étape
        if (
          data.currentStep !== "verification_proprete" &&
          data.currentStep !== "verification_proprete_retour"
        ) {
          console.log(
            "[CleanlinessInspection] L'étape actuelle ne correspond pas à celle attendue:",
            data.currentStep
          );

          // Si l'étape actuelle est checklist, on considère qu'on peut quand même afficher la page
          // car cela signifie que l'API a automatiquement avancé l'étape après l'enregistrement des dégradations
          if (
            data.currentStep === "checklist" ||
            data.currentStep === "checklist_retour"
          ) {
            console.log(
              "[CleanlinessInspection] L'étape est déjà passée à la checklist. Affichage des données de propreté existantes."
            );
            setAssignment(data);

            // Initialiser les données de propreté si elles existent déjà
            if (isReturn && data.finalState?.cleanliness) {
              setCleanliness({
                interior: data.finalState.cleanliness.interior || false,
                exterior: data.finalState.cleanliness.exterior || false,
                cabine: data.finalState.cleanliness.cabine || false,
                comments: data.finalState.cleanliness.comments || "",
                photos: data.finalState.cleanliness.photos || [],
              });
            } else if (data.initialState?.cleanliness) {
              setCleanliness({
                interior: data.initialState.cleanliness.interior || false,
                exterior: data.initialState.cleanliness.exterior || false,
                cabine: data.initialState.cleanliness.cabine || false,
                comments: data.initialState.cleanliness.comments || "",
                photos: data.initialState.cleanliness.photos || [],
              });
            }

            // Aucune erreur car nous permettons de visualiser les données même si l'étape est déjà avancée
            setError(null);
          }
          // Si nous sommes dans un retour mais pas à la bonne étape, tenter de synchroniser
          else if (
            isReturn &&
            (data.currentStep === "selection_chauffeur" ||
              data.currentStep === "inspection_exterieure_retour")
          ) {
            console.log(
              "[CleanlinessInspection] Tentative de passage à l'étape verification_proprete_retour"
            );
            try {
              // Effectuer plusieurs nextStep pour arriver à l'étape verification_proprete_retour
              let updatedAssignment = data;
              let attempts = 0;
              const maxAttempts = 5; // Limiter le nombre de tentatives

              while (
                updatedAssignment.currentStep !==
                  "verification_proprete_retour" &&
                attempts < maxAttempts
              ) {
                console.log(
                  "[CleanlinessInspection] Passage à l'étape suivante depuis:",
                  updatedAssignment.currentStep
                );
                updatedAssignment = await AssignmentService.nextStep(id);
                attempts++;
                if (
                  updatedAssignment.currentStep ===
                  "verification_proprete_retour"
                ) {
                  break;
                }
              }

              setAssignment(updatedAssignment);

              // Si après plusieurs tentatives, nous ne sommes toujours pas à la bonne étape
              if (
                updatedAssignment.currentStep !== "verification_proprete_retour"
              ) {
                setError(
                  "Impossible d'atteindre l'étape de vérification de propreté (retour)"
                );
              }
            } catch (syncError) {
              console.error(
                "[CleanlinessInspection] Erreur lors de la synchronisation des étapes:",
                syncError
              );
              setError(
                "Impossible de synchroniser l'étape avec la page actuelle"
              );
            }
          }
          // Si nous ne sommes pas dans un retour mais à l'étape d'inspection extérieure, passer à l'étape suivante
          else if (!isReturn && data.currentStep === "inspection_exterieure") {
            console.log(
              "[CleanlinessInspection] Tentative de passage à l'étape verification_proprete"
            );
            try {
              const updatedAssignment = await AssignmentService.nextStep(id);
              setAssignment(updatedAssignment);

              if (updatedAssignment.currentStep !== "verification_proprete") {
                setError(
                  "Impossible d'atteindre l'étape de vérification de propreté"
                );
              }
            } catch (syncError) {
              console.error(
                "[CleanlinessInspection] Erreur lors de la synchronisation des étapes:",
                syncError
              );
              setError(
                "Impossible de synchroniser l'étape avec la page actuelle"
              );
            }
          } else {
            // Si nous sommes dans une autre étape non compatible
            setAssignment(data);
            setError(
              `L'étape actuelle (${data.currentStep}) ne correspond pas à la vérification de propreté`
            );
          }
        } else {
          // L'étape est correcte, initialiser normalement
          setAssignment(data);

          // Initialiser les données de propreté si elles existent déjà
          if (isReturn && data.finalState?.cleanliness) {
            setCleanliness({
              interior: data.finalState.cleanliness.interior || false,
              exterior: data.finalState.cleanliness.exterior || false,
              cabine: data.finalState.cleanliness.cabine || false,
              comments: data.finalState.cleanliness.comments || "",
              photos: data.finalState.cleanliness.photos || [],
            });
          } else if (data.initialState?.cleanliness) {
            setCleanliness({
              interior: data.initialState.cleanliness.interior || false,
              exterior: data.initialState.cleanliness.exterior || false,
              cabine: data.initialState.cleanliness.cabine || false,
              comments: data.initialState.cleanliness.comments || "",
              photos: data.initialState.cleanliness.photos || [],
            });
          }
        }
      } catch (err) {
        console.error(
          "[CleanlinessInspection] Erreur lors du chargement de l'affectation:",
          err
        );
        setError("Impossible de charger les détails de l'affectation");
        enqueueSnackbar("Erreur lors du chargement de l'affectation", {
          variant: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAssignment();
  }, [id, enqueueSnackbar, isReturn]);

  // Mettre à jour l'état de la propreté
  const handleCleanlinessChange = (
    field: keyof CleanlinessState,
    value: boolean | null | string
  ) => {
    setCleanliness({
      ...cleanliness,
      [field]: value,
    });
  };

  // Simuler la prise de photo
  const handleTakePhoto = () => {
    // Dans une application réelle, ici on activerait l'appareil photo
    setIsCameraActive(true);

    // Simuler la prise d'une photo avec un délai
    setTimeout(() => {
      const mockPhotoUrl = `https://source.unsplash.com/random/800x600?car,clean&t=${Date.now()}`;
      setCleanliness({
        ...cleanliness,
        photos: [...cleanliness.photos, mockPhotoUrl],
      });
      setIsCameraActive(false);
      enqueueSnackbar("Photo ajoutée avec succès", { variant: "success" });
    }, 1500);
  };

  // Ajouter une photo
  const handleAddPhoto = () => {
    if (!newPhoto.trim()) {
      enqueueSnackbar("Veuillez entrer une URL valide", {
        variant: "warning",
      });
      return;
    }

    setCleanliness((prev) => ({
      ...prev,
      photos: Array.isArray(prev.photos)
        ? [...prev.photos, newPhoto]
        : [newPhoto],
    }));
    setNewPhoto("");
  };

  // Supprimer une photo
  const handleRemovePhoto = (index: number) => {
    const updatedPhotos = [...cleanliness.photos];
    updatedPhotos.splice(index, 1);
    setCleanliness({
      ...cleanliness,
      photos: updatedPhotos,
    });
  };

  // Soumettre les données de propreté
  const handleSubmit = async () => {
    if (!id) return;

    try {
      setSubmitting(true);
      console.log("[CleanlinessInspection] Début du processus de soumission");

      // Récupérer d'abord l'état actuel de l'affectation pour avoir les données à jour
      console.log(
        "[CleanlinessInspection] Récupération de l'affectation avec ID:",
        id
      );
      const currentAssignment = await AssignmentService.getById(id);
      console.log(
        "[CleanlinessInspection] État actuel de l'affectation:",
        currentAssignment
      );
      setAssignment(currentAssignment);

      // Vérifier que l'étape actuelle est bien celle de l'inspection de propreté
      // ou si on est déjà passé à l'étape suivante (checklist)
      console.log(
        "[CleanlinessInspection] Vérification de l'étape actuelle:",
        currentAssignment.currentStep
      );

      const isPropreteStep =
        currentAssignment.currentStep === "verification_proprete" ||
        currentAssignment.currentStep === "verification_proprete_retour";

      const isChecklistStep =
        currentAssignment.currentStep === "checklist" ||
        currentAssignment.currentStep === "checklist_retour";

      if (!isPropreteStep && !isChecklistStep) {
        // Mise à jour de l'erreur et notification
        const errorMessage =
          "L'étape actuelle ne correspond pas à la vérification de propreté. L'affectation sera rechargée.";
        console.error("[CleanlinessInspection] Erreur d'étape:", errorMessage);
        setError(errorMessage);
        enqueueSnackbar(errorMessage, { variant: "error" });
        setSubmitting(false);
        return;
      }

      // Selon que c'est un retour ou une affectation initiale
      const isPropreteRetour =
        currentAssignment.currentStep === "verification_proprete_retour" ||
        currentAssignment.currentStep === "checklist_retour";
      console.log("[CleanlinessInspection] Est un retour?", isPropreteRetour);

      // Si l'étape est déjà à checklist, ne pas essayer de mettre à jour les données de propreté
      const isAlreadyInNextStep = isChecklistStep;

      if (!isAlreadyInNextStep) {
        // Envoyer les données de propreté
        console.log(
          "[CleanlinessInspection] Données de propreté à envoyer:",
          cleanliness
        );
        if (isPropreteRetour) {
          console.log(
            "[CleanlinessInspection] Envoi de l'état de propreté final"
          );
          await AssignmentService.updateCleanliness(id, cleanliness, true);
          enqueueSnackbar("État de propreté final enregistré avec succès", {
            variant: "success",
          });
        } else {
          console.log(
            "[CleanlinessInspection] Envoi de l'état de propreté initial"
          );
          await AssignmentService.updateCleanliness(id, cleanliness);
          enqueueSnackbar("État de propreté initial enregistré avec succès", {
            variant: "success",
          });
        }
      } else {
        console.log(
          "[CleanlinessInspection] Étape déjà avancée à checklist, pas besoin d'envoyer de données"
        );
      }

      // Passer à l'étape suivante SEULEMENT après l'envoi des données (ou si déjà à l'étape suivante)
      let updatedAssignment = currentAssignment;

      if (!isAlreadyInNextStep) {
        console.log("[CleanlinessInspection] Passage à l'étape suivante");
        updatedAssignment = await AssignmentService.nextStep(id);
        console.log(
          "[CleanlinessInspection] Nouvel état après nextStep:",
          updatedAssignment
        );
      } else {
        console.log(
          "[CleanlinessInspection] Déjà à l'étape suivante, pas besoin d'appeler nextStep"
        );
      }

      // Mettre à jour l'état local avec la dernière version
      setAssignment(updatedAssignment);

      // Redirection vers la page Checklist
      console.log("[CleanlinessInspection] Redirection vers la page Checklist");
      if (isPropreteRetour) {
        navigate(`/checklist/${id}?return=true`);
      } else {
        navigate(`/checklist/${id}`);
      }
    } catch (err) {
      console.error(
        "[CleanlinessInspection] Erreur lors de l'enregistrement de l'état de propreté:",
        err
      );
      enqueueSnackbar("Erreur lors de l'enregistrement de l'état de propreté", {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Gérer le retour à l'étape précédente
  const handlePreviousStep = async () => {
    if (!id) return;

    try {
      setSubmitting(true);
      console.log(
        "[CleanlinessInspection] Début du processus de retour à l'étape précédente"
      );

      // Récupérer d'abord l'état actuel pour s'assurer que nous avons les dernières données
      console.log("[CleanlinessInspection] Récupération de l'état actuel");
      const currentAssignment = await AssignmentService.getById(id);
      console.log(
        "[CleanlinessInspection] État actuel avant previousStep:",
        currentAssignment
      );

      // Passer à l'étape précédente
      console.log("[CleanlinessInspection] Appel de previousStep");
      const updatedAssignment = await AssignmentService.previousStep(id);
      console.log(
        "[CleanlinessInspection] État après previousStep:",
        updatedAssignment
      );

      // Mettre à jour l'affectation locale
      setAssignment(updatedAssignment);

      // Redirection vers la page précédente
      if (currentAssignment.currentStep === "verification_proprete_retour") {
        console.log(
          "[CleanlinessInspection] Redirection vers inspection-exterieure (retour)"
        );
        // Si nous sommes dans la phase de retour, revenir à l'inspection extérieure retour
        navigate(`/inspection-exterieure/${id}?return=true`);
      } else {
        console.log(
          "[CleanlinessInspection] Redirection vers inspection-exterieure"
        );
        // Si nous sommes dans la phase initiale, revenir à l'inspection extérieure
        navigate(`/inspection-exterieure/${id}`);
      }
    } catch (err) {
      console.error(
        "[CleanlinessInspection] Erreur lors du retour à l'étape précédente:",
        err
      );
      enqueueSnackbar("Erreur lors du retour à l'étape précédente", {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          width: "100vw",
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#f9f9f9",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !assignment) {
    return (
      <Box
        sx={{
          width: "100vw",
          minHeight: "100vh",
          backgroundColor: "#f9f9f9",
          py: { xs: 2, md: 3 },
          px: { xs: 1, md: 2 },
        }}
      >
        <Container maxWidth={false} sx={{ maxWidth: 1600 }}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" color="error">
              {error || "Affectation non trouvée"}
            </Typography>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/assignments")}
              sx={{ mt: 2 }}
            >
              Retour à la liste
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100vw",
        minHeight: "100vh",
        backgroundColor: "#f9f9f9",
        py: { xs: 2, md: 3 },
        px: { xs: 1, md: 2 },
      }}
    >
      <Container maxWidth={false} sx={{ maxWidth: 1600 }}>
        <Box sx={{ p: 3, minHeight: "100vh" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "flex-start" : "center",
              gap: isMobile ? 2 : 0,
              mb: 4,
            }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/assignments")}
              sx={{ mr: 2 }}
              size={isMobile ? "medium" : "large"}
            >
              Retour
            </Button>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                fontSize: { xs: "1.8rem", md: "2.2rem" },
              }}
            >
              Vérification de la propreté {isReturn ? "(retour)" : "(entrée)"}
            </Typography>
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body1">
              Veuillez vérifier la propreté du véhicule et noter toute
              observation.
              <strong>
                {" "}
                Dans les prochaines étapes, vous devrez compléter une checklist
                technique et recueillir les signatures nécessaires.
              </strong>
            </Typography>
          </Alert>

          <Paper
            sx={{
              p: 3,
              mb: 4,
              borderRadius: 2,
              backgroundImage:
                "linear-gradient(to right bottom, #ffffff, #f9f9f9)",
            }}
            elevation={3}
          >
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Information sur l'affectation
              </Typography>
              <Grid container spacing={2}>
                <Grid sx={{ gridColumn: { xs: "1 / 13", sm: "1 / 7" } }}>
                  <Typography variant="body1">
                    <strong>Véhicule:</strong> {assignment?.vehiclePlate}
                  </Typography>
                </Grid>
                <Grid sx={{ gridColumn: { xs: "1 / 13", sm: "7 / 13" } }}>
                  <Typography variant="body1">
                    <strong>Chauffeur:</strong> {assignment?.driverFirstName}{" "}
                    {assignment?.driverLastName}
                  </Typography>
                </Grid>
                <Grid sx={{ gridColumn: { xs: "1 / 13", sm: "1 / 7" } }}>
                  <Typography variant="body1">
                    <strong>Date:</strong>{" "}
                    {assignment?.date &&
                      new Date(assignment.date).toLocaleDateString("fr-FR")}
                  </Typography>
                </Grid>
                <Grid sx={{ gridColumn: { xs: "1 / 13", sm: "7 / 13" } }}>
                  <Typography variant="body1">
                    <strong>Kilométrage:</strong> {assignment?.startMileage} km
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              État de propreté
            </Typography>

            <Box sx={{ mb: 4 }}>
              <Grid container spacing={3}>
                {cleanlinessItems.map((item) => (
                  <Grid
                    sx={{
                      gridColumn: { xs: "span 12", sm: "span 6", md: "span 4" },
                    }}
                    key={item.key}
                  >
                    <Card
                      sx={{
                        width: "100%",
                        height: "100%",
                        borderRadius: 2,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                        border:
                          item.isConform === true
                            ? "1px solid #4caf50"
                            : item.isConform === false
                            ? "1px solid #f44336"
                            : "1px solid #e0e0e0",
                        transition: "all 0.3s ease",
                      }}
                    >
                      <CardHeader
                        title={item.label}
                        titleTypographyProps={{
                          variant: "h6",
                          fontWeight: 500,
                        }}
                        sx={{
                          backgroundColor:
                            item.isConform === true
                              ? "rgba(76, 175, 80, 0.1)"
                              : item.isConform === false
                              ? "rgba(244, 67, 54, 0.1)"
                              : "rgba(0, 0, 0, 0.03)",
                          borderBottom: "1px solid #eee",
                          pb: 1,
                        }}
                      />
                      <CardContent sx={{ pt: 2 }}>
                        <ButtonGroup fullWidth variant="outlined" size="large">
                          <Button
                            color="success"
                            variant={
                              cleanliness[item.key] === true
                                ? "contained"
                                : "outlined"
                            }
                            onClick={() =>
                              handleCleanlinessChange(item.key, true)
                            }
                            startIcon={<CheckIcon />}
                            sx={{ py: 1 }}
                          >
                            Conforme
                          </Button>
                          <Button
                            color="error"
                            variant={
                              cleanliness[item.key] === false
                                ? "contained"
                                : "outlined"
                            }
                            onClick={() =>
                              handleCleanlinessChange(item.key, false)
                            }
                            startIcon={<ClearIcon />}
                            sx={{ py: 1 }}
                          >
                            Non conforme
                          </Button>
                        </ButtonGroup>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Photos de propreté
              </Typography>

              <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleTakePhoto}
                  startIcon={<PhotoCameraIcon />}
                  disabled={isCameraActive}
                >
                  {isCameraActive ? "Capture en cours..." : "Prendre une photo"}
                </Button>

                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleAddPhoto}
                  startIcon={<AddIcon />}
                >
                  Ajouter une URL
                </Button>

                {isCameraActive && (
                  <CircularProgress size={24} sx={{ ml: 2 }} />
                )}
              </Box>

              {!isCameraActive && (
                <TextField
                  fullWidth
                  size="small"
                  label="URL de la photo"
                  variant="outlined"
                  value={newPhoto}
                  onChange={(e) => setNewPhoto(e.target.value)}
                  placeholder="Entrez l'URL d'une image..."
                  sx={{ mb: 2 }}
                />
              )}

              <Grid container spacing={2}>
                {Array.isArray(cleanliness.photos) &&
                  cleanliness.photos.map((photo, index) => (
                    <Grid
                      container
                      component="div"
                      sx={{
                        gridColumn: {
                          xs: "span 12",
                          sm: "span 6",
                          md: "span 4",
                        },
                      }}
                      key={index}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          position: "relative",
                          mb: 2,
                          border: "1px solid #ddd",
                          borderRadius: 1,
                          p: 1,
                          height: "100%",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
                        }}
                      >
                        <img
                          src={photo}
                          alt={`Photo de propreté ${index + 1}`}
                          style={{
                            width: "100%",
                            height: "auto",
                            borderRadius: "4px",
                          }}
                        />
                        <Button
                          color="error"
                          variant="contained"
                          onClick={() => handleRemovePhoto(index)}
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            minWidth: "auto",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                          }}
                          size="small"
                        >
                          <CloseIcon />
                        </Button>
                      </Box>
                    </Grid>
                  ))}
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Commentaires
              </Typography>
              <Grid sx={{ gridColumn: { xs: "1 / 13" } }}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={cleanliness.comments || ""}
                  onChange={(e) =>
                    handleCleanlinessChange("comments", e.target.value)
                  }
                  placeholder="Ajouter des commentaires ou observations supplémentaires..."
                />
              </Grid>
            </Box>
          </Paper>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexDirection: isMobile ? "column" : "row",
              gap: isMobile ? 2 : 0,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate("/assignments")}
              fullWidth={isMobile}
            >
              Annuler
            </Button>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexDirection: isMobile ? "column" : "row",
              }}
            >
              <Button
                variant="outlined"
                color="secondary"
                onClick={handlePreviousStep}
                disabled={submitting}
                fullWidth={isMobile}
              >
                Étape précédente
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={submitting}
                fullWidth={isMobile}
              >
                {submitting ? "Enregistrement..." : "Suivant"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default CleanlinessInspection;
